import { router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { activityRowConfig } from "@/lib/activity-feed"
import { MOTION_BEATS } from "@/lib/motion"
import { formatRelativeTime } from "@/lib/relative-time"
import { cn } from "@/lib/utils"
import { notificationPath } from "@/routes"
import type { NotificationRow as NotificationRowType } from "@/types/notifications"

interface ActivityFeedRowProps {
  notification: NotificationRowType
  /**
   * True when this row arrived live this session (id not present at mount).
   * Renders the NEW pill + accent bar + "NOW" time and plays the one-shot
   * `live-pulse` entrance (CSS-only; collapses under reduced-motion but
   * keeps the NEW pill + accent — UX Spec §4).
   */
  isNew?: boolean
  onMarkRead?: () => void
}

/**
 * OKS-548 — A single Activity-feed recognition row (creator surface).
 *
 * Evolves the shipped `NotificationRow` structure (same optimistic-read
 * click → `PATCH /notifications/:id` → controller redirect, same a11y
 * posture) but sources its color-coded icon badge + accessible type label
 * from `activityRowConfig(event_key)` (KD-4) rather than the collapsed
 * serializer `type`. This keeps cheer→flame distinct from kudo→heart and
 * approved→green-check distinct from mission→target without changing the
 * shared serializer contract or the bell panel.
 *
 * Click semantics (UX Spec §1): optimistic local read flip, then
 * `router.patch(notificationPath(id))` — the controller marks read
 * (idempotent) and redirects to `target_url` or back. The FE never
 * navigates manually.
 *
 * Accessibility: native `<button>` (keyboard-activatable), `<time>` with a
 * machine-readable `dateTime`, unread signalled by BOTH a left dot AND a
 * background tint (never color alone), and the icon badge carries a
 * localized `aria-label` so the type is conveyed to AT.
 */
export function ActivityFeedRow({
  notification,
  isNew = false,
  onMarkRead,
}: ActivityFeedRowProps) {
  const { t, i18n } = useTranslation()
  const [optimisticRead, setOptimisticRead] = useState(
    notification.read_at !== null,
  )

  const isUnread = !optimisticRead
  const {
    icon: TypeIcon,
    badgeClass,
    labelKey,
  } = activityRowConfig(notification.event_key)

  function handleClick() {
    if (isUnread) {
      setOptimisticRead(true)
      onMarkRead?.()
    }

    router.patch(
      notificationPath(notification.id),
      {},
      {
        preserveScroll: false,
        onError: () => setOptimisticRead(notification.read_at !== null),
      },
    )
  }

  return (
    <button
      type="button"
      data-testid={`activity-row-${notification.id}`}
      data-event-key={notification.event_key}
      data-state={isUnread ? "unread" : "read"}
      data-new={isNew ? "true" : undefined}
      onClick={handleClick}
      className={cn(
        "group relative flex w-full items-start gap-3 border-b px-4 py-4 text-left transition-colors outline-none last:border-b-0",
        "focus-visible:bg-muted/60",
        // New-arrival treatment: left accent bar + tinted background + the
        // one-shot live-pulse entrance (motion-safe, reduced-motion keeps
        // the static accent/pill). Otherwise the neutral unread accent.
        isNew
          ? cn(
              "border-l-primary bg-primary/5 border-l-2",
              MOTION_BEATS["live-pulse"],
            )
          : isUnread
            ? "bg-primary/5"
            : "hover:bg-muted/40 bg-transparent",
      )}
    >
      {isUnread && !isNew && (
        <span
          aria-hidden="true"
          data-testid={`activity-row-${notification.id}-unread-dot`}
          className="bg-primary absolute top-1/2 left-1.5 size-1.5 -translate-y-1/2 rounded-full"
        />
      )}

      <span
        data-testid={`activity-row-${notification.id}-icon`}
        aria-label={t(labelKey)}
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full",
          badgeClass,
        )}
      >
        <TypeIcon aria-hidden="true" className="size-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <p
            className={cn(
              "line-clamp-2 flex-1 text-sm leading-tight",
              isUnread ? "font-semibold" : "font-medium",
            )}
          >
            {notification.title}
          </p>
          {isNew && (
            <span
              data-testid={`activity-row-${notification.id}-new-pill`}
              className="bg-primary text-primary-foreground shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase"
            >
              {t("activity.new")}
            </span>
          )}
        </div>
        <p className="text-muted-foreground line-clamp-1 text-xs">
          {notification.body}
        </p>
        <time
          dateTime={notification.created_at}
          className="text-muted-foreground/80 mt-1 text-[11px] tracking-wide uppercase"
        >
          {isNew
            ? t("activity.now")
            : formatRelativeTime(notification.created_at, t, i18n.language)}
        </time>
      </div>
    </button>
  )
}
